import useOrganizationSettings from 'hooks/use-organization-settings'
import useUpdateOrganizationSettings from 'hooks/use-update-organization-settings'
import { Organization } from 'interfaces/organization'
import { useEffect, useState } from 'react'
import { Card, Button } from 'react-bootstrap'
import { toast } from 'react-hot-toast'

interface OrganizationSettingsCardProps {
  orgId: string
  org?: Organization
}

interface SettingOption {
  id: string
  label: string
  value: boolean
  onChange: (checked: boolean) => void
}

export const OrganizationSettingsCard = ({
  orgId,
  org,
}: OrganizationSettingsCardProps) => {
  const [sendLoginCodeEmails, setSendLoginCodeEmails] = useState(true)
  const [sendTransactionalEmails, setSendTransactionalEmails] = useState(true)
  const [sendMarketingEmails, setSendMarketingEmails] = useState(true)
  const [sendNewsletterEmails, setSendNewsletterEmails] = useState(true)
  const [enableTeamLogoInQr, setEnableTeamLogoInQr] = useState(false)
  const [hideBillingPage, setHideBillingPage] = useState(false)
  const [hideLinqPhone, setHideLinqPhone] = useState(false)
  const [lftForeverEnabled, setLftForeverEnabled] = useState(false)

  const updateOrganizationSettings = useUpdateOrganizationSettings({ orgId })

  const { data: organizationSettings } = useOrganizationSettings({
    orgId: orgId,
  })

  useEffect(() => {
    setSendLoginCodeEmails(!!organizationSettings?.send_login_code_emails)
    setSendTransactionalEmails(
      !!organizationSettings?.send_transactional_emails
    )
    setSendMarketingEmails(!!organizationSettings?.send_marketing_emails)
    setSendNewsletterEmails(!!organizationSettings?.send_newsletter_emails)
    setEnableTeamLogoInQr(!!organizationSettings?.enable_team_logo_in_qr)
    setHideBillingPage(!!organizationSettings?.hide_billing)
    setHideLinqPhone(!!organizationSettings?.hide_linq_phone)
    setLftForeverEnabled(!!organizationSettings?.lft_forever_enabled)
  }, [organizationSettings])

  const handleUpdateSettings = () => {
    updateOrganizationSettings.mutate({
      ...organizationSettings,
      send_login_code_emails: sendLoginCodeEmails,
      send_transactional_emails: sendTransactionalEmails,
      send_marketing_emails: sendMarketingEmails,
      send_newsletter_emails: sendNewsletterEmails,
      enable_team_logo_in_qr: enableTeamLogoInQr,
      hide_billing: hideBillingPage,
      hide_linq_phone: hideLinqPhone,
      lft_forever_enabled: lftForeverEnabled,
    })
  }

  const settingsOptions: SettingOption[] = [
    {
      id: 'enableTeamLogoInQrCheck',
      label: 'Enable team logo in QR code for this organization',
      value: enableTeamLogoInQr,
      onChange: setEnableTeamLogoInQr,
    },
    {
      id: 'hideBillingPage',
      label: 'Hide Billing Page',
      value: hideBillingPage,
      onChange: setHideBillingPage,
    },
    {
      id: 'hideLinqPhone',
      label: 'Hide Linq Phone',
      value: hideLinqPhone,
      onChange: setHideLinqPhone,
    },
    {
      id: 'sendLoginCodeEmailsCheck',
      label: "Send login code emails to organization's members",
      value: sendLoginCodeEmails,
      onChange: setSendLoginCodeEmails,
    },
    {
      id: 'sendTransactionalEmailsCheck',
      label: "Send general transactional emails to organization's members",
      value: sendTransactionalEmails,
      onChange: setSendTransactionalEmails,
    },
    {
      id: 'sendMarketingEmailsCheck',
      label: "Send marketing emails to organization's members",
      value: sendMarketingEmails,
      onChange: setSendMarketingEmails,
    },
    {
      id: 'sendNewslettersEmailsCheck',
      label: "Send newsletters emails to organization's members",
      value: sendNewsletterEmails,
      onChange: setSendNewsletterEmails,
    },
    {
      id: 'lftForeverEnabledCheck',
      label: 'Enable LFT forever for this organization',
      value: lftForeverEnabled,
      onChange: setLftForeverEnabled,
    },
  ]

  return (
    <Card className="mt-4">
      <Card.Header>
        <h4>Organization Settings</h4>
      </Card.Header>
      <Card.Body>
        <Button
          onClick={handleUpdateSettings}
          variant="success"
          className="mb-3 me-3"
        >
          Update Settings
        </Button>
        {settingsOptions.map((option) => (
          <div key={option.id} className="form-check mt-4">
            <input
              className="form-check-input"
              type="checkbox"
              checked={option.value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                option.onChange(event.target.checked)
              }
              id={option.id}
            />
            <label className="form-check-label" htmlFor={option.id}>
              {option.label}
            </label>
          </div>
        ))}
      </Card.Body>
    </Card>
  )
}
